import { useCallback, useEffect, useRef, useState } from "react";
import { VERSION_AVISO_ROSTRO } from "@huayruro/shared";
import { apiRequest } from "./api";
import { getToken } from "./auth";

// Clientes por cara v1 (cadena huayruro-ojo-oido-agentes, tramo 10; sección 18 del plan del ojo).
// La webcam del mostrador manda huellas al Worker (nunca fotos) y el Worker deja SUGERENCIAS. El Mostrador las lee
// aquí y quien atiende responde «Sí» o «No»: reconocer es sugerir, nunca afirmar (D-C6). Todo online; si no hay
// red, simplemente no aparece la tarjeta (la venta sigue por la cola offline).

export type SugerenciaRostro = {
  id: string;
  cliente_id: string;
  nombre: string;
  alias: string | null;
  parecido: number;
  visto_en: string;
  misma_botica: number;
  botica_cliente: string;
};

export type EstadoRostro = {
  estado: "sin_cara" | "esperando_fotos" | "registrada";
  consentimiento_id: string | null;
  registrado_en: string | null;
  registro_abierto?: boolean; // false = el aviso sigue en borrador: no se muestra ni se guarda permiso (tramo 11)
  huellas: number;
};

// Con webcam en la botica se pregunta seguido (la persona está frente al mostrador ahora); sin webcam, casi nunca.
const CADA_MS_CON_WEBCAM = 3_000;
const CADA_MS_SIN_WEBCAM = 60_000;

const opts = (extra: Record<string, unknown> = {}) => ({ token: getToken(), reintentos: 0, ...extra });

export function useSugerenciasRostro(activo: boolean): {
  sugerencias: SugerenciaRostro[];
  responder: (id: string, si: boolean) => Promise<{ cliente_id: string; misma_botica: boolean }>;
} {
  const [sugerencias, setSugerencias] = useState<SugerenciaRostro[]>([]);
  const [conWebcam, setConWebcam] = useState(false);
  const vivo = useRef(true);

  const cargar = useCallback(async () => {
    if (!activo || document.visibilityState !== "visible") return;
    try {
      const r = await apiRequest<{ sugerencias: SugerenciaRostro[]; webcam: boolean }>("/clientes/rostro/sugerencias", opts());
      if (!vivo.current) return;
      setSugerencias(r.sugerencias ?? []);
      setConWebcam(r.webcam === true);
    } catch {
      /* sin red o sin permiso: no hay tarjeta; la venta no depende de esto */
    }
  }, [activo]);

  useEffect(() => {
    vivo.current = true;
    if (!activo) {
      setSugerencias([]);
      return () => {
        vivo.current = false;
      };
    }
    void cargar();
    const t = window.setInterval(() => void cargar(), conWebcam ? CADA_MS_CON_WEBCAM : CADA_MS_SIN_WEBCAM);
    return () => {
      vivo.current = false;
      window.clearInterval(t);
    };
  }, [activo, cargar, conWebcam]);

  const responder = useCallback(async (id: string, si: boolean) => {
    // Se quita de la vista enseguida: la respuesta ya es de quien atiende.
    setSugerencias((s) => s.filter((x) => x.id !== id));
    return apiRequest<{ cliente_id: string; misma_botica: boolean }>(`/clientes/rostro/sugerencias/${id}/${si ? "si" : "no"}`, opts({ method: "POST", body: {} }));
  }, []);

  return { sugerencias, responder };
}

export function estadoRostro(clienteId: string): Promise<EstadoRostro> {
  return apiRequest<EstadoRostro>(`/clientes/${clienteId}/rostro`, opts());
}

// El cliente tocó «Acepto» en la pantalla: se manda la versión del aviso que se le mostró.
export function aceptarRostro(clienteId: string): Promise<{ consentimiento_id: string; texto_version: string }> {
  return apiRequest(`/clientes/${clienteId}/rostro/consentimiento`, opts({ method: "POST", body: { acepto: true, texto_version: VERSION_AVISO_ROSTRO } }));
}

// Panel › Clientes › Reconocimiento.
export type RegistroRostro = {
  cliente_id: string;
  nombre: string;
  botica: string;
  consentimiento_id: string;
  texto_version: string;
  aceptado_en: string;
  registrado_en: string;
  operador: string | null;
  huellas: number;
  calidad: number;
};

export type PresenciaRostro = {
  id: string;
  cliente_id: string;
  nombre: string;
  botica_vista: string;
  botica_cliente: string;
  visto_en: string;
  parecido: number;
  estado: "sugerida" | "confirmada" | "rechazada";
  respondido_por: string | null;
};
